import * as Sentry from '@sentry/react'
import { signOut } from 'aws-amplify/auth'
import axios from 'axios'
import { toast } from 'react-toastify'
import { getToken, HEADER_PREFIX } from 'utils/auth'
import { getSentryHeaders } from 'utils/sentry'

import { NetworkError } from '../error'
import { logoutUser } from './auth'
import { validateResponse } from './fetchHandlers'

const INTERNET_TOAST_ID = 'INTERNET_TOAST_ID'

// FETCH VALIDATE

function getRequestBody(requestParams) {
  if (!requestParams.body) return

  try {
    return JSON.parse(requestParams.body)
  } catch {
    return requestParams.body
  }
}
async function fetchAndValidateResponse(url, requestParams) {
  const res = await axios({
    url,
    method: requestParams.method.toLowerCase(),
    headers: {
      ...getSentryHeaders(),
      ...requestParams.headers,
    },
    data: getRequestBody(requestParams),
    responseType: requestParams.responseType,
    timeout: 0,
    signal: requestParams?.signal,
    ...requestParams,
  })

  return validateResponse(res)
}

async function makeHttpRequest(url, requestParams) {
  try {
    return await fetchAndValidateResponse(url, requestParams)
  } catch (error) {
    if (
      window?.navigator?.onLine !== true &&
      !toast.isActive(INTERNET_TOAST_ID)
    ) {
      toast.error('Reconnect to the Internet', {
        toastId: INTERNET_TOAST_ID,
      })
      throw new Error('Reconnect to the Internet')
    }

    if (error.response) {
      error.status = error.response.status
    }

    if (error.response?.data?.detail === 'User inactive or deleted.') {
      try {
        await signOut()
      } catch (error) {
        // Do nothing
      }
      try {
        await logoutUser()
      } catch (error) {
        // Do nothing
      }
    }

    // Skip Sentry reporting for canceled requests
    if (error.message === 'canceled' || error.name === 'AbortError') {
      throw error
    }

    Sentry.captureException(error, {
      extra: {
        ...(error instanceof NetworkError ? error.toJSON() : {}),
        url,
        requestParams,
        browserOnlineStatus: window?.navigator?.onLine,
      },
      tags: {
        type:
          error.status >= 400 && error.status < 500
            ? 'client_error'
            : 'server_error',
        status: error.status,
        endpoint: url,
      },
    })

    throw error
  }
}

// FETCH MAKE CALLS
export async function makeGetRequest(url, extraProps = {}) {
  const userToken = await getToken()
  return makeHttpRequest(url, {
    method: 'GET',
    headers: {
      'content-type': 'application/json',
      Authorization: userToken ? `${HEADER_PREFIX} ${userToken}` : 'None',
    },
    ...extraProps,
  })
}

export async function makePostRequest(url, extraProps = {}) {
  const userToken = await getToken()
  return makeHttpRequest(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: userToken ? `${HEADER_PREFIX} ${userToken}` : 'None',
    },
    ...extraProps,
  })
}

export async function makePostFileRequest(url, extraProps = {}) {
  const userToken = await getToken()
  return makeHttpRequest(url, {
    method: 'POST',
    headers: {
      Authorization: userToken ? `${HEADER_PREFIX} ${userToken}` : 'None',
    },
    ...extraProps,
  })
}

export async function makePatchRequest(url, extraProps = {}) {
  const userToken = await getToken()
  return makeHttpRequest(url, {
    method: 'PATCH',
    headers: {
      'content-type': 'application/json',
      Authorization: userToken ? `${HEADER_PREFIX} ${userToken}` : 'None',
    },
    ...extraProps,
  })
}

export async function makePutRequest(url, extraProps = {}) {
  const userToken = await getToken()
  return makeHttpRequest(url, {
    method: 'PUT',
    headers: {
      'content-type': 'application/json',
      Authorization: userToken ? `${HEADER_PREFIX} ${userToken}` : 'None',
    },
    ...extraProps,
  })
}

export async function makeDeleteRequest(url, extraProps = {}) {
  const userToken = await getToken()
  return makeHttpRequest(url, {
    method: 'DELETE',
    headers: {
      'content-type': 'application/json',
      Authorization: userToken ? `${HEADER_PREFIX} ${userToken}` : 'None',
    },
    ...extraProps,
  })
}

export async function makeOptionsRequest(url, extraProps = {}) {
  const userToken = await getToken()
  return makeHttpRequest(url, {
    method: 'OPTIONS',
    headers: {
      'content-type': 'application/json',
      Authorization: userToken ? `${HEADER_PREFIX} ${userToken}` : 'None',
    },
    ...extraProps,
  })
}

export async function makeGetDownloadRequest(url, extraProps = {}) {
  const userToken = await getToken()
  return makeHttpRequest(url, {
    method: 'GET',
    headers: {
      Authorization: userToken ? `${HEADER_PREFIX} ${userToken}` : 'None',
    },
    responseType: 'blob',
    ...extraProps,
  })
}
